/**
 * Validated environment configuration.
 *
 * The only place in the application allowed to read `import.meta.env` — an
 * ESLint rule enforces it. Everything else imports `env` from here and gets a
 * fully typed object that is guaranteed to exist.
 *
 * Parsing happens at module load, so a missing or malformed variable fails
 * immediately with the offending key named, rather than surfacing later as a
 * request to `undefined/api/v1/auth/me/`. The same schema also runs at build time
 * (see vite.config.ts), so in practice a bad value never reaches a browser.
 */
import { envSchema, formatEnvIssues, type Env } from './envSchema';

const parsed = envSchema.safeParse(import.meta.env);

if (!parsed.success) {
  throw new Error(
    `Configuración inválida en .env.local:\n${formatEnvIssues(parsed.error)}\n\n` +
      `Copia .env.example y completa los valores. Ver docs/LOCAL_SETUP.md.`,
  );
}

export const env = parsed.data;
export type { Env };

/**
 * Vite's own build-mode flags. Re-exported here so that `import.meta.env`
 * genuinely has exactly one reader in the codebase, and the ESLint rule that
 * enforces it needs no exceptions.
 */
export const isDev = import.meta.env.DEV;
export const isProd = import.meta.env.PROD;

/**
 * Vite's base URL, always slash-terminated. Not a deploy-time variable — it is
 * the `base` the app is served under — but it must live here anyway, for the
 * same reason as `isDev`/`isProd`: one reader for `import.meta.env`.
 */
export const baseUrl = import.meta.env.BASE_URL;
